/**
 * Führt `worker` für jedes Element aus `items` aus, mit maximal `limit` gleichzeitig laufenden
 * Aufrufen. Grund: die Katalog-Batch-Suche (catalog-batch-suggest-dialog.tsx) darf nicht N
 * LV-Positionen als N gleichzeitige HTTP-Requests gegen
 * GET /api/softec-db/materials/search abfeuern — das wäre unnötige Serverlast bei großen LVs.
 *
 * Reine, netzwerkfreie Funktion: der eigentliche Netzwerkaufruf ist Sache des Callers (`worker`).
 * Ergebnisreihenfolge entspricht IMMER der Eingabereihenfolge, unabhängig davon, welcher Aufruf
 * zuerst fertig wird (kein `Promise.all` auf unsortierten Batches). Ein fehlschlagender
 * `worker`-Aufruf lässt das Gesamtergebnis rejecten (Fail-Fast) — der Caller fängt Fehler pro
 * Position deshalb selbst im `worker` ab, wenn eine einzelne fehlgeschlagene Suche die übrigen
 * Positionen nicht blockieren soll (siehe Verwendung in catalog-batch-suggest-dialog.tsx).
 */
export interface RunWithConcurrencyLimitOptions {
  /**
   * P2-3 (Fix, Codex-Fund): der bisherige Generation-Guard in catalog-batch-suggest-dialog.tsx
   * unterdrückte nur State-UPDATES einer veraltet gewordenen Batch-Suche (Dialog neu geöffnet/
   * geschlossen, Angebot gewechselt) — die Worker HIER dequeueten und fetchten trotzdem ALLE
   * restlichen Positionen weiter, weil `runNext()` das gar nicht wissen konnte. Ein großes LV
   * feuerte dadurch nach einer Invalidierung noch hunderte unnötige Requests ab, und ein erneutes
   * Öffnen startete einen ZWEITEN Batch parallel zum (weiterlaufenden) alten.
   *
   * Wird VOR JEDEM Start eines neuen Worker-Aufrufs geprüft — liefert sie `false`, wird für die
   * restlichen Elemente KEIN weiterer Worker mehr gestartet (deren Platz im Ergebnis-Array bleibt
   * `undefined`). Bricht bereits LAUFENDE Worker-Aufrufe nicht selbst ab (kein Promise.race) —
   * dafür ist `signal` gedacht, das der Worker selbst an seinen fetch()/o.ä. durchreicht.
   */
  shouldContinue?: () => boolean;
  /**
   * Optionales AbortSignal, das an JEDEN Worker-Aufruf durchgereicht wird (3. Argument) — der
   * Worker entscheidet selbst, was er damit tut (typischerweise `fetch(url, { signal })`, damit
   * bereits laufende Requests bei einer Invalidierung tatsächlich abgebrochen werden, statt nur
   * ihr Ergebnis zu ignorieren).
   */
  signal?: AbortSignal;
}

export async function runWithConcurrencyLimit<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number, signal?: AbortSignal) => Promise<R>,
  options: RunWithConcurrencyLimitOptions = {},
): Promise<R[]> {
  const effectiveLimit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 1;
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function runNext(): Promise<void> {
    while (nextIndex < items.length) {
      // P2-3: vor JEDEM neuen Start prüfen, ob dieser Lauf überhaupt noch aktuell ist — stale
      // (z.B. Dialog neu geöffnet/geschlossen) bricht das Nachrücken NEUER Worker sofort ab,
      // statt die komplette Restliste noch abzuarbeiten.
      if (options.shouldContinue && !options.shouldContinue()) return;
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await worker(items[currentIndex], currentIndex, options.signal);
    }
  }

  const workerCount = Math.min(effectiveLimit, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => runNext()));
  return results;
}
